import { confirmPageUnload } from '../../lib/UnsavedEditArea';

/** Учётная запись браузера сменилась, страница ещё не ушла: `retry` — уйти снова. */
export interface IAccountSwitchState {
    retry: () => void;
}

let state: IAccountSwitchState | null = null;
let hasUnsavedInput: (() => boolean) | null = null;
let releaseUnload: (() => void) | null = null;
const listeners = new Set<() => void>();

const publish = (next: IAccountSwitchState | null) => {
    state = next;
    listeners.forEach((listener) => listener());
};

/** Экран «Учётная запись сменилась»: виден, пока страница не ушла (или «Остаться» в вопросе браузера). */
export const accountSwitchStore = {
    subscribe: (listener: () => void): (() => void) => {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },
    getSnapshot: (): IAccountSwitchState | null => state,
};

/**
 * Правка страницы, которую нельзя бросить перезагрузкой (несохранённая или автосохраняемая), — её показывает
 * `AccountSwitchScreen` из корневой области правок.
 */
export function watchUnsavedInput(check: () => boolean): () => void {
    hasUnsavedInput = check;
    return () => {
        if (hasUnsavedInput === check) hasUnsavedInput = null;
    };
}

const leave = (url?: string) => (url ? window.location.assign(url) : window.location.reload());

/**
 * Эта вкладка сменила учётную запись (переключение, «Добавить») после вопроса о несохранённом: страница уходит —
 * перезагрузка текущего адреса или `url`. Вопрос браузера о правках области не повторяется; если уход всё же отменён
 * (своё предупреждение страницы), интерфейс прежней учётной записи закрыт экраном «Учётная запись сменилась».
 */
export function leaveAfterAccountChange(url?: string): void {
    releaseUnload ??= confirmPageUnload();
    const retry = () => leave(url);
    publish({ retry });
    retry();
}

/**
 * Учётную запись сменила соседняя вкладка (R9): без несохранённой правки — перезагрузка текущего адреса, с ней —
 * экран «Учётная запись сменилась» вместо слепой перезагрузки: правка ушла бы уже от имени другой учётной записи.
 */
export function reloadAfterForeignAccountChange(): void {
    const retry = () => leave();
    publish({ retry });
    if (!hasUnsavedInput?.()) retry();
}

/** @internal Тестам: экран скрыт, правка не отслеживается. */
export function resetAccountSwitch(): void {
    releaseUnload?.();
    releaseUnload = null;
    hasUnsavedInput = null;
    publish(null);
}
